// From a realtime event to the query cache (docs/plan/12-frontend.md §5): realtime is a hint, REST
// is the truth, so an event never writes data, it marks the queries it concerns as stale (the
// ones on screen refetch at once, the others on their next use). The generated query keys start
// with the request URL, so a query is matched by a fragment of that URL.
import type { QueryClient } from '@tanstack/react-query';

/** Whether the first key of a query (its URL) contains one of the fragments. */
function matcher(fragments: readonly string[]) {
  return (query: { queryKey: readonly unknown[] }): boolean => {
    const [key] = query.queryKey;
    return typeof key === 'string' && fragments.some((fragment) => key.includes(fragment));
  };
}

/** One invalidation for all the fragments (a single pass over the cache). */
export function invalidateFragments(queryClient: QueryClient, fragments: readonly string[]) {
  if (fragments.length === 0) return Promise.resolve();
  return queryClient.invalidateQueries({ predicate: matcher(fragments) });
}

/** Drops the queries from the cache (access to them was revoked). */
export function removeFragments(queryClient: QueryClient, fragments: readonly string[]) {
  if (fragments.length === 0) return;
  queryClient.removeQueries({ predicate: matcher(fragments) });
}

/** The notification bell and the Activity feed (and the unread count, part of the bell's query). */
export const NOTIFICATION_FRAGMENTS = ['/api/v1/me/notifications', '/api/v1/me/activity'] as const;

export const documentFragments = (spaceId: string): string[] => [
  `/api/v1/spaces/${spaceId}/documents`,
  `/api/v1/spaces/${spaceId}/folders`,
];

/** What an approval changes: the flows (incl. the "to decide" lists), the documents (locked while a flow runs) and what follows the flow. */
export const approvalFragments = (spaceId: string): string[] => [
  `/api/v1/spaces/${spaceId}/approval-flows`,
  `/api/v1/spaces/${spaceId}/documents`,
  '/deliverables',
  '/phases',
];

export const taskFragments = (projectId: string, taskId: string): string[] => [
  `/projects/${projectId}/tasks`,
  `/tasks/${taskId}`,
];

/**
 * What an El Fatoora status change touches: the invoice (its TTN chip), its e-invoicing state and
 * TEIF link (their keys all contain the invoice's URL), and the invoice lists of the organisation
 * (matched on their exact URL, so the other invoices on screen are left alone).
 */
export function invalidateEinvoice(queryClient: QueryClient, orgId: string, invoiceId: string) {
  const listUrl = `/api/v1/orgs/${orgId}/invoices`;
  const detail = matcher([`${listUrl}/${invoiceId}`]);
  return queryClient.invalidateQueries({
    predicate: (query) => detail(query) || query.queryKey[0] === listUrl,
  });
}
